import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Search, Paperclip, ExternalLink } from "lucide-react";
import {
  adminListCosecQuotes,
  adminUpdateCosecQuote,
  adminSignCosecAttachment,
} from "@/lib/api/cosec.functions";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/cosec-quotes",
)({
  component: AdminCosecQuotesPage,
});

type Status = "new" | "contacted" | "quoted" | "completed";
const STATUSES: Status[] = ["new", "contacted", "quoted", "completed"];

const statusColor: Record<Status, string> = {
  new: "bg-blue-100 text-blue-800",
  contacted: "bg-amber-100 text-amber-800",
  quoted: "bg-purple-100 text-purple-800",
  completed: "bg-emerald-100 text-emerald-800",
};

function csvEscape(v: unknown): string {
  if (v === null || v === undefined) return "";
  const s = String(v).replace(/"/g, '""');
  return /[",\n\r]/.test(s) ? `"${s}"` : s;
}

function AdminCosecQuotesPage() {
  const list = useServerFn(adminListCosecQuotes);
  const update = useServerFn(adminUpdateCosecQuote);
  const sign = useServerFn(adminSignCosecAttachment);
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-cosec-quotes"],
    queryFn: () => list(),
  });

  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"all" | Status>("all");

  const filtered = useMemo(() => {
    const rows: any[] = data?.rows ?? [];
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (status !== "all" && r.status !== status) return false;
      if (!needle) return true;
      return [
        r.full_name,
        r.email,
        r.phone,
        r.company,
        r.registration,
        r.sub_service,
        r.reference,
        r.details,
      ]
        .filter(Boolean)
        .some((v: string) => String(v).toLowerCase().includes(needle));
    });
  }, [data, q, status]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: 0, new: 0, contacted: 0, quoted: 0, completed: 0 };
    (data?.rows ?? []).forEach((r: any) => {
      c.all += 1;
      if (c[r.status] !== undefined) c[r.status] += 1;
    });
    return c;
  }, [data]);

  const mUpdate = useMutation({
    mutationFn: (vars: { id: string; status?: Status; admin_notes?: string }) =>
      update({ data: vars }),
    onSuccess: () => {
      toast.success("Updated");
      qc.invalidateQueries({ queryKey: ["admin-cosec-quotes"] });
    },
    onError: (e: any) => toast.error(e?.message || "Update failed"),
  });

  function exportCsv() {
    const headers = [
      "reference",
      "created_at",
      "status",
      "full_name",
      "email",
      "phone",
      "company",
      "registration",
      "sub_service",
      "contact_method",
      "details",
      "attachments",
      "contacted_at",
      "quoted_at",
      "completed_at",
      "admin_notes",
    ];
    const lines = [headers.join(",")];
    for (const r of filtered) {
      const atts = Array.isArray(r.attachments)
        ? r.attachments.map((a: any) => a.name).join("; ")
        : "";
      lines.push(
        [
          r.reference,
          r.created_at,
          r.status,
          r.full_name,
          r.email,
          r.phone,
          r.company,
          r.registration,
          r.sub_service,
          r.contact_method,
          r.details,
          atts,
          r.contacted_at,
          r.quoted_at,
          r.completed_at,
          r.admin_notes,
        ]
          .map(csvEscape)
          .join(","),
      );
    }
    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cosec-quotes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function openAttachment(path: string) {
    try {
      const res = await sign({ data: { path } });
      window.open(res.url, "_blank", "noopener");
    } catch (e: any) {
      toast.error(e?.message || "Could not open file");
    }
  }

  if (error)
    return <p className="text-sm text-red-600">Forbidden or load failed.</p>;

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-display text-xl text-navy">
            Company Secretarial quote requests
          </h2>
          <p className="text-xs text-muted-foreground">
            Track and respond to CIPC / MOI / governance enquiries.
          </p>
        </div>
        <button
          onClick={exportCsv}
          className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-sm hover:bg-secondary/60"
        >
          <Download className="h-4 w-4" /> Export CSV ({filtered.length})
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, ref, company…"
            className="w-72 rounded-md border border-input bg-background pl-8 pr-3 py-2 text-sm"
          />
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {(["all", ...STATUSES] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`rounded-md border px-2.5 py-1 text-xs capitalize ${
                status === s
                  ? "border-navy bg-navy text-navy-foreground"
                  : "border-border bg-card hover:bg-secondary/60"
              }`}
            >
              {s} ({counts[s] ?? 0})
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <p className="text-sm text-muted-foreground">Loading…</p>
      )}

      <div className="space-y-3">
        {filtered.map((r: any) => (
          <div
            key={r.id}
            className="rounded-lg border border-border bg-card p-4 shadow-soft"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${
                      statusColor[r.status as Status]
                    }`}
                  >
                    {r.status}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {r.reference}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleString()}
                  </span>
                </div>
                <p className="mt-1 font-medium text-navy">
                  {r.full_name}{" "}
                  <span className="text-xs font-normal text-muted-foreground">
                    · {r.email}
                    {r.phone ? ` · ${r.phone}` : ""}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  Service: {r.sub_service || "—"} · Contact:{" "}
                  {r.contact_method}
                  {r.company ? ` · ${r.company}` : ""}
                  {r.registration ? ` · CIPC ${r.registration}` : ""}
                </p>
                <details className="mt-2 text-sm">
                  <summary className="cursor-pointer text-xs text-navy hover:underline">
                    View details
                  </summary>
                  <p className="mt-2 whitespace-pre-wrap text-foreground/80">
                    {r.details}
                  </p>
                  {Array.isArray(r.attachments) && r.attachments.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-navy">
                        Attachments
                      </p>
                      <ul className="mt-1 space-y-1">
                        {r.attachments.map((a: any) => (
                          <li key={a.path} className="text-xs">
                            <button
                              onClick={() => openAttachment(a.path)}
                              className="inline-flex items-center gap-1 text-navy hover:text-gold"
                            >
                              <Paperclip className="h-3 w-3" />
                              {a.name}
                              <ExternalLink className="h-3 w-3" />
                              <span className="text-muted-foreground">
                                ({Math.round((a.size || 0) / 1024)} KB)
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </details>
              </div>

              <div className="flex flex-col gap-2">
                <select
                  value={r.status}
                  onChange={(e) =>
                    mUpdate.mutate({
                      id: r.id,
                      status: e.target.value as Status,
                    })
                  }
                  className="rounded-md border border-border bg-background px-2 py-1 text-sm"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <NotesEditor
                  initial={r.admin_notes || ""}
                  onSave={(notes) =>
                    mUpdate.mutate({ id: r.id, admin_notes: notes })
                  }
                />
              </div>
            </div>
          </div>
        ))}
        {data && filtered.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No quote requests match your filters.
          </p>
        )}
      </div>
    </section>
  );
}

function NotesEditor({
  initial,
  onSave,
}: {
  initial: string;
  onSave: (notes: string) => void;
}) {
  const [val, setVal] = useState(initial);
  const [open, setOpen] = useState(false);
  if (!open)
    return (
      <button
        onClick={() => setOpen(true)}
        className="rounded-md border border-border bg-card px-2 py-1 text-xs hover:bg-secondary/60"
      >
        {initial ? "Edit notes" : "Add notes"}
      </button>
    );
  return (
    <div className="space-y-1">
      <textarea
        value={val}
        onChange={(e) => setVal(e.target.value)}
        rows={3}
        className="w-64 rounded-md border border-border bg-background px-2 py-1 text-xs"
        placeholder="Internal notes (not sent to client)"
      />
      <div className="flex gap-1">
        <button
          onClick={() => {
            onSave(val);
            setOpen(false);
          }}
          className="rounded bg-navy px-2 py-1 text-xs text-navy-foreground"
        >
          Save
        </button>
        <button
          onClick={() => {
            setVal(initial);
            setOpen(false);
          }}
          className="rounded border border-border px-2 py-1 text-xs"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
